// NATS connect entrypoint · thin facade over `@nats-io/nats-core`'s
// `wsconnect`. Pins the three connect-config overrides (pingInterval=5s,
// maxReconnectAttempts=-1, noEcho=true) via `buildConnectConfig`.
//
// Downstream tasks (T8 connectivity state machine, T17140 heartbeat
// budget, T6006 RN cellular branch) compose on top of this; the actual
// transport open is left to the caller so we can inject a fake in tests.

import type { ConnectionOptions, NatsConnection } from '@nats-io/nats-core';
import {
  type BuildConnectConfigOptions,
  buildConnectConfig,
  DEFAULT_PING_INTERVAL_MS,
  INFINITE_RECONNECT,
} from './connect-config.js';
import { whenFirstPaintComplete } from './first-paint-gate.js';

/**
 * T2207 · h3 L1230 — Two dangerous `@nats-io/nats-core` defaults that
 * compile fine + ship green tests but produce silent staleness in production.
 *
 * Both are pinned here as a literal-typed constant so a stray `wsconnect(...)`
 * call without these fields fails the FQ-NATS-DEFAULTS-001 source-grep rule.
 *
 * Override 1 · pingInterval (default 120_000 ms → 5_000 ms):
 *   At 2-min pings, DEGRADED flips ~6 min after the socket dies. Override
 *   to 5 s for ~15 s p99 detection; RN adapter swings to 30 s on cellular
 *   via `pingIntervalMs` on `BuildConnectConfigOptions`.
 *
 * Override 2 · maxReconnectAttempts (default 10 → -1):
 *   After 10 failed attempts (~30 s sustained outage), the client silently
 *   disables realtime for the rest of the tab's lifetime. -1 = infinite;
 *   the connectivity FSM (T8 + T2208) owns capped-backoff ladder.
 */
export const NATS_CONNECT_OVERRIDES = {
  pingInterval: DEFAULT_PING_INTERVAL_MS,
  maxReconnectAttempts: INFINITE_RECONNECT,
} as const;

/**
 * T2207 · env-aware `ConnectionOptions` builder. Thin facade over
 * `buildConnectConfig` that pins the two L1230 overrides into the returned
 * shape regardless of env tuning. `NATS_PING_INTERVAL_MS` env var still
 * overrides `pingInterval` at deployment time (read inside
 * `resolvePingInterval`).
 */
export interface BuildConnectOptionsEnv {
  readonly servers: readonly string[];
  readonly authenticator: () => string;
  readonly pingIntervalMs?: number;
}

export function buildConnectOptions(env: BuildConnectOptionsEnv): ConnectionOptions {
  return buildConnectConfig(env);
}

/**
 * Open a NATS connection using the canonical v2 connect-config.
 *
 * `connectFn` is the dependency-injected `wsconnect` (or a test fake).
 * In production wiring, callers pass `wsconnect` from `@nats-io/nats-core`.
 * Keeping it injected avoids a hard import of the ws-transport module from
 * the React Native bundle (T11 platform adapter).
 */
export async function connect(
  opts: BuildConnectConfigOptions,
  connectFn: (config: ConnectionOptions) => Promise<NatsConnection>,
): Promise<NatsConnection> {
  // T6002 · SSR-safe gate. The WS MUST NOT open before first-paint-complete:
  // an event arriving between dehydrate (server) and hydrate (client commit)
  // can trigger `setQueryData` BEFORE React commits the hydrated state —
  // verified failure class in TanStack/query #9642. The gate is a no-op on
  // RN once the platform adapter calls `markFirstPaintComplete()` on the
  // first JS frame after `SdkScreen` mount.
  await whenFirstPaintComplete();
  const config = buildConnectConfig(opts);
  return connectFn(config);
}

// ─────────────────────────────────────────────────────────────────────────
// T303 · Connection-promise singleton · cold-mount handshake race fix
// ─────────────────────────────────────────────────────────────────────────
//
// File 05 §1259 fix. `@nats-io/nats-core` does NOT dedupe concurrent
// `wsconnect()` calls — two parallel callers both open WebSockets, one
// wins assignment to `natsClient.client`, the other is orphaned and held
// open until heartbeat-miss timeout (~15s). Naive
//
//     if (!client) client = connect(...)
//
// has a TOCTOU window between the `await` boundary in `connect()` and the
// subsequent assignment.
//
// Correct form: capture a SINGLE in-flight promise BEFORE the first await
// using `??=`. The second caller awaits the same promise. On failure,
// clear the slot so retries can try again.
//
// HMR-safe: pinned on `globalThis.__morfi_connectionPromise` so a
// dev-server hot reload doesn't fork a parallel handshake against an
// already-warm client. Key constant lives in ./globals.ts (single
// source-of-truth — FR8 fix #1).

import { getJwtStore } from '../auth/jwt-store.js';
import { beginConnection } from './connection-ledger.js';
import { CONNECTION_PROMISE_GLOBAL_KEY, CONNECTION_SERVERS_GLOBAL_KEY } from './globals.js';
import { markIntentionalClose } from './intentional-close.js';

const PROMISE_PIN_KEY = CONNECTION_PROMISE_GLOBAL_KEY;
const SERVERS_PIN_KEY = CONNECTION_SERVERS_GLOBAL_KEY;

type GlobalWithConnPromise = typeof globalThis & {
  [PROMISE_PIN_KEY]?: Promise<NatsConnection> | undefined;
  [SERVERS_PIN_KEY]?: string | undefined;
};

/**
 * Race-guarded singleton entry-point.
 *
 * Two concurrent callers receive the SAME `Promise<NatsConnection>`. A
 * rejected promise clears the pin so the next caller can retry.
 */
/**
 * Bound on the whole handshake once the first-paint gate has released.
 * nats-core's own `timeout` (10 s) only covers the socket open; a degraded
 * gateway that opens the socket and never sends `INFO` hangs the connect
 * promise forever, which arms nothing (the ladder fires on REJECTION) and
 * installs nothing (every recovery handler is gated on a client). The
 * watchdog turns that hang into a rejection; the orphaned socket is closed
 * if it ever completes.
 */
export const HANDSHAKE_WATCHDOG_MS = 15_000;

export class HandshakeTimeoutError extends Error {
  constructor(ms: number) {
    super(`NATS handshake did not complete within ${ms}ms`);
    this.name = 'HandshakeTimeoutError';
  }
}

export interface ConnectWatchdogOpts {
  readonly timeoutMs?: number;
  readonly setTimeoutFn?: (h: () => void, ms: number) => unknown;
  readonly clearTimeoutFn?: (h: unknown) => void;
}

/**
 * Race-guarded singleton entry-point.
 *
 * Two concurrent callers receive the SAME `Promise<NatsConnection>`. A
 * rejected promise (including the handshake watchdog) clears the pin so
 * the next caller can retry.
 */
export function connectSingleton(
  opts: BuildConnectConfigOptions,
  connectFn: (config: ConnectionOptions) => Promise<NatsConnection>,
  watchdog: ConnectWatchdogOpts = {},
): Promise<NatsConnection> {
  const g = globalThis as GlobalWithConnPromise;
  const servers = opts.servers.join(',');
  // A network switch remounts the provider against another server; handing it
  // the old server's connection mixes the networks' realtime and block-meta.
  if (g[PROMISE_PIN_KEY] !== undefined && g[SERVERS_PIN_KEY] !== servers) {
    retirePinnedConnection(g);
  }
  if (g[PROMISE_PIN_KEY] !== undefined) return g[PROMISE_PIN_KEY];
  g[SERVERS_PIN_KEY] = servers;
  let pinned: Promise<NatsConnection> | undefined;
  pinned = (async (): Promise<NatsConnection> => {
    const setT =
      watchdog.setTimeoutFn ??
      ((h: () => void, ms: number): unknown =>
        (globalThis as { setTimeout: typeof setTimeout }).setTimeout(h, ms));
    const clearT =
      watchdog.clearTimeoutFn ??
      ((h: unknown): void =>
        (globalThis as { clearTimeout: typeof clearTimeout }).clearTimeout(
          h as ReturnType<typeof setTimeout>,
        ));
    const timeoutMs = watchdog.timeoutMs ?? HANDSHAKE_WATCHDOG_MS;
    let timer: unknown = null;
    let attempt: Promise<NatsConnection> | null = null;
    // Devtool ledger: every client this tab opens, under which identity.
    const ledger = beginConnection(
      opts.servers[0] ?? '',
      getJwtStore().current()?.kind ?? 'unknown',
    );
    try {
      // The first-paint gate is not part of the handshake budget: on RN it
      // waits for the first screen mount, which can legitimately be slow.
      await whenFirstPaintComplete();
      const config = buildConnectConfig(opts);
      attempt = connectFn(config);
      const expiry = new Promise<never>((_resolve, reject) => {
        timer = setT(() => reject(new HandshakeTimeoutError(timeoutMs)), timeoutMs);
      });
      const nc = await Promise.race([attempt, expiry]);
      ledger.connected(nc);
      return nc;
    } catch (err) {
      ledger.failed(err);
      // Clear the slot so the next caller — including the SDKProvider
      // bootstrap/flip retry seam — can re-attempt with a FRESHLY-MINTED
      // JWT. Retry is owned by the CALLER, not here: a rejection is NOT
      // necessarily transport (an expired JWT yields `Authorization
      // Violation`, which needs a re-mint), so `SDKProvider` classifies it,
      // reports it, and arms the real re-mint+reconnect ladder.
      if (g[PROMISE_PIN_KEY] === pinned) delete g[PROMISE_PIN_KEY];
      if (err instanceof HandshakeTimeoutError && attempt !== null) {
        // The stalled handshake may still complete later; never leak that
        // socket next to the one the retry opens.
        void attempt.then(
          (late) => {
            // Ledger it first so the orphan shows up as opened-then-closed.
            ledger.connected(late);
            markIntentionalClose(late);
            void late.close().catch((): void => {});
          },
          (): void => {},
        );
      }
      throw err;
    } finally {
      if (timer !== null) clearT(timer);
    }
  })();
  g[PROMISE_PIN_KEY] = pinned;
  return pinned;
}

function retirePinnedConnection(g: GlobalWithConnPromise): void {
  const previous = g[PROMISE_PIN_KEY];
  delete g[PROMISE_PIN_KEY];
  void previous?.then(
    (nc) => {
      markIntentionalClose(nc);
      void nc.close().catch((): void => {});
    },
    (): void => {},
  );
}

/**
 * Drop the in-flight connect-promise pin so the NEXT `connectSingleton`
 * call enters the cold path and opens a brand-new WebSocket.
 *
 * Production callers MUST `close()`/`drain()` the previously-resolved
 * connection first — otherwise its socket is orphaned. The legitimate
 * production callers are the identity-flip cleanup (`preReplaceCleanup`)
 * and the recovery rebuild (`createRemintReconnect`), both of which tear
 * the old connection down before resetting.
 */
export function resetConnectionSingleton(): void {
  const g = globalThis as GlobalWithConnPromise;
  delete g[PROMISE_PIN_KEY];
}

/**
 * Reset the singleton — test alias of {@link resetConnectionSingleton}.
 * Retained for the existing test + `preReplaceCleanup` call sites.
 */
export const __resetConnectionSingletonForTests: () => void = resetConnectionSingleton;

export type { BuildConnectConfigOptions } from './connect-config.js';
export {
  buildConnectConfig,
  DEFAULT_PING_INTERVAL_MS,
  INFINITE_RECONNECT,
  RECONNECT_JITTER_MS,
  RECONNECT_JITTER_TLS_MS,
  RECONNECT_TIME_WAIT_MS,
  resolvePingInterval,
} from './connect-config.js';
